import { format } from "date-fns";
import { FC, ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import UAParser from "ua-parser-js";

import { Notifications } from "@livechat/design-system-icons";
import { Alert, Button, Icon, Link, SpacingToken, Text } from "@livechat/design-system-react-components";

import { BadgeType } from "components/basic";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { StyledBadge } from "components/notifications-bar/components/styles";
import {
  SettingsTableColumn as RawColumn,
  SettingsTableRow as Row,
  SettingsTable as Table,
} from "components/settings-table";
import { HelpCenter } from "constants/help-center";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { IDeviceListItem } from "interfaces/entities/devices";
import { getItem } from "services/local-storage";
import {
  AgentActions,
  selectHasSeenEnableNotificationBanner,
  selectIsAgentDeviceSubscriptionFetching,
  selectIsViewer,
} from "store/entities/agents";
import { selectApplicationServerKey } from "store/entities/application-settings/selectors";
import { selectMaxWebpushSubscriptionsPerAgent } from "store/entities/license";
import { getCurrentAgentId } from "store/features/session";
import { withVar } from "themes/helpers";

import { enableNotifications } from "../../../../../serviceWorkerRegistration";
import { AgentFormSection } from "../AgentFormSection";
import { AgentFormSectionId } from "../constants";

import BrowserNotificationsAlert from "./BrowserNotificationsAlert";
import CheckBrowserNotifications from "./CheckBrowserNotifications";
import NotificationsPreferences from "./NotificationsPreferences";
import { disableLabelStyles, enableNotificationButtonStyles } from "./styles";
import { isPushNotificationSupported } from "./utils";

const Column: FC<{
  children: ReactNode;
}> = ({ children }) => <RawColumn paddingDesktop="1em 1.5em">{children}</RawColumn>;

interface ITableHeaderProps {
  column: number;
  columns: number;
}

const TableHeader: FC<ITableHeaderProps> = ({ column, columns }) => {
  const index = (column - 1) % columns;
  if (index === 0) {
    return <>Browser</>;
  }

  if (index === 1) {
    return <>Created</>;
  }

  if (index === 2) {
    return <>Actions</>;
  }

  return null;
};

const DeviceListItem: FC<IDeviceListItem> = ({ agentId, createdAt, userAgent, ID }) => {
  const dispatch = useDispatch();

  const currentId = getItem(`agent-id:${agentId}`);
  const ua = UAParser(userAgent);
  const uaDate = new Date(createdAt);

  const formatedDate = format(uaDate, "D MMM, YYYY");

  const formatedTime = format(uaDate, "h:mm A");

  const handleDisableDevice = (): void => {
    dispatch(AgentActions.unsubscribeAgentDevice({ deviceId: ID }));
    trackEngagement("Browser notifications: Disable device notifications button click", EventPlace.Agents);
  };

  return (
    <Row>
      <Column>
        <Flex alignItems="center" gap="4px">
          <Text>{ua.browser.name || "Unknown browser"}</Text>
          {currentId === ID ? <StyledBadge type={BadgeType.Gray}>Current</StyledBadge> : null}
        </Flex>
        <Text>on {ua.os.name ? ua.os.name : "unknown system"}</Text>
      </Column>
      <Column>
        <Text>{formatedDate}</Text>
        <Text>{formatedTime}</Text>
      </Column>
      <Column>
        <Text css={disableLabelStyles} onClick={handleDisableDevice}>
          Disable
        </Text>
      </Column>
    </Row>
  );
};

interface IDeviceList {
  deviceList: IDeviceListItem[];
  agentId: string;
}

const DeviceList: FC<IDeviceList> = ({ agentId, deviceList }) => (
  <Table columns={3} marginTop="20px" marginBottom="0px" columnSizes="4fr 3fr 2fr" headers={TableHeader}>
    {deviceList &&
      deviceList.length !== 0 &&
      deviceList.map(item => (
        <DeviceListItem
          key={item.ID}
          ID={item.ID}
          createdAt={item.createdAt}
          userAgent={item.userAgent}
          type={item.type}
          agentId={agentId}
        />
      ))}
  </Table>
);

interface IProps {
  agentId: string;
  webpushSubs: IDeviceListItem[];
}

const BrowserNotificationsManagement: FC<IProps> = ({ agentId, webpushSubs }) => {
  const dispatch = useDispatch();
  const [testAlertVisible, setTestAlertVisible] = useState(true);
  const [permissionAlertVisible, setPermissionAlertVisible] = useState(false);

  const maxWebpushSubs = useSelector(selectMaxWebpushSubscriptionsPerAgent);
  const currentAgentId = useSelector(getCurrentAgentId);
  const applicationServerKey = useSelector(selectApplicationServerKey);
  const isViewer = useSelector(selectIsViewer);
  const fetchingAgentSubState = useSelector(selectIsAgentDeviceSubscriptionFetching);
  const hasSeenEnableNotificationBanner = useSelector(selectHasSeenEnableNotificationBanner);

  const enableComponent = maxWebpushSubs && !isViewer;
  const isYourAccount = currentAgentId === agentId;
  const isWebpushSupported = isPushNotificationSupported();
  const isLimitReached = webpushSubs.length === maxWebpushSubs;
  const currentBrowserSubscribed = webpushSubs.some(item => item.ID === getItem(`agent-id:${agentId}`));

  const shouldDisplayBanner = currentBrowserSubscribed && testAlertVisible;
  const shouldSeeEnableButton = isYourAccount && isWebpushSupported && !isLimitReached && !currentBrowserSubscribed;

  const handleEnableNotifications = async (): Promise<void> => {
    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    const subscription = await enableNotifications(applicationServerKey);
    if (Notification.permission !== "granted") {
      setPermissionAlertVisible(true);
      setTestAlertVisible(false);
      return;
    }

    setPermissionAlertVisible(false);
    setTestAlertVisible(true);

    if (subscription) {
      // @ts-expect-error TS(2322)
      dispatch(AgentActions.subscribeAgentDevice({ subscription }));
      trackEngagement("Browser notifications: Enable notifications button click", EventPlace.Agents);
    }

    if (!hasSeenEnableNotificationBanner) {
      dispatch(
        AgentActions.setFlag({ name: "hasSeenEnableNotificationBanner_bba3563bc528023d6d1353cf26b0c796", value: true })
      );
    }
  };

  const handleLearnMoreClick = (): void => {
    trackEngagement("Browser notifications: Learn more button click", EventPlace.Agents);
  };

  if (!enableComponent) {
    return null;
  }

  return (
    <AgentFormSection header={{ id: AgentFormSectionId.BrowserNotifications, component: "Browser notifications" }}>
      <Block marginBottom={withVar(SpacingToken.Spacing5)}>
        <Text>
          Enable browser notifications when there are any updates in the ticket. These notifications are set up for a
          specific agent.{" "}
          <Link
            bold={false}
            target="_blank"
            rel="noopener noreferrer"
            href={HelpCenter.ENABLE_BROWSER_NOTIFICATIONS}
            onClick={handleLearnMoreClick}
          >
            Learn more
          </Link>
        </Text>
      </Block>
      <BrowserNotificationsAlert agentId={agentId} webpushSubscription={webpushSubs} />
      {shouldSeeEnableButton && (
        <Button
          kind="secondary"
          size="compact"
          icon={<Icon source={Notifications} />}
          css={enableNotificationButtonStyles}
          onClick={handleEnableNotifications}
          loading={fetchingAgentSubState}
        >
          Enable browser notifications
        </Button>
      )}
      {permissionAlertVisible && (
        <Block marginTop={withVar(SpacingToken.Spacing5)}>
          <Alert kind="warning">
            Your browser has browser notifications blocked. Read how to enable browser notifications.
            <Block marginTop={withVar(SpacingToken.Spacing3)}>
              <Button
                kind="plain"
                target="_blank"
                rel="noopener noreferrer"
                href={`${HelpCenter.ENABLE_BROWSER_NOTIFICATIONS}/#the-enable-browser-notifications-button-doesnt-seem-to-work`}
              >
                Read instructions
              </Button>
            </Block>
          </Alert>
        </Block>
      )}
      {shouldDisplayBanner && <CheckBrowserNotifications deviceList={webpushSubs} />}
      <NotificationsPreferences deviceList={webpushSubs} />
      {webpushSubs?.length !== 0 && (
        <>
          <Text>List of browsers with browser notifications enabled</Text>
          {/* @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'string'. */}
          <DeviceList agentId={currentAgentId} deviceList={webpushSubs} />
        </>
      )}
    </AgentFormSection>
  );
};

export default BrowserNotificationsManagement;
